/**
 * **A14 · The shelter roll** (Stream A, Paper.dc.html): printed at the admin station from the
 * day's two lists — role holders on site and volunteers on shift, by area, each with a tick box —
 * for a lightning hold when the phones are down. The public is the plan's sweep; this is our own
 * people. Read by the station's own session, and on the print log in the desk's name.
 */
import type { ReactNode } from 'react';
import { formatDate, formatTime } from '@cider/ui';
import { useLoad } from '../views/admin/shared.js';
import { usePrinted } from './printed.js';
import { SheetPages, type SheetLine } from './sheets.js';

interface Here {
  asOf: string;
  onSite: { personId: string; name: string; role: string; visitorId: string | null }[];
  onShift: { personId: string; name: string; area: string; since: string }[];
}

export function ShelterRoll(): ReactNode {
  const d = useLoad<Here>('/volunteers/booth/whos-here');
  usePrinted('shelter-roll', !!d.data, { station: true });
  if (d.error) return <p role="alert">{d.error}</p>;
  if (!d.data) return <p>Reading who is here…</p>;
  const h = d.data;
  const roles = h.onSite.filter((x) => !x.visitorId);
  const areas = [...new Set(h.onShift.map((x) => x.area))].sort();
  const lines: SheetLine[] = [
    { head: `ON SITE · ROLE HOLDERS · ${String(roles.length)}` },
    ...(roles.length
      ? roles.map((x) => ({ key: x.personId, cells: [x.name, x.role, null], tick: true }))
      : [{ cells: ['0 role holders said I’m here today.'] }]),
    ...areas.flatMap((area) => {
      const mine = h.onShift.filter((x) => x.area === area);
      return [
        { head: `ON SHIFT · ${area.toUpperCase()} · ${String(mine.length)}` },
        ...mine.map((x) => ({
          key: `${area}${x.personId}`,
          cells: [x.name, `in ${formatTime(x.since)}`, null],
          tick: true,
        })),
      ];
    }),
    ...(areas.length ? [] : [{ head: 'ON SHIFT · 0' }, { cells: ['0 volunteers checked in.'] }]),
  ];
  const day = formatDate(h.asOf);
  return (
    <SheetPages
      doc={{
        size: 'letter',
        title: 'Shelter roll · lightning hold',
        sub: `${day} · printed ${formatTime(h.asOf)} · tick who is accounted for in the hall · say the time`,
        qr: `ROLL ${h.asOf.slice(0, 10)}`,
        name: 'Shelter roll · this is who we know is on site and on shift — the public is the plan’s sweep',
        cols: ['wide', 'mid', 'when'],
        lines,
        sign: ['Roll taken by · name and time', 'Hold lifted by Dispatch · time'],
      }}
    />
  );
}
